import React, {useEffect, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Alert, Button, Empty, Popconfirm, Select, Space, Spin, Tag, message} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined, TableOutlined} from "@ant-design/icons";
import {ApiCheckerboardService} from "../../../../../shared/api";
import {complexOptions, loadComplexes} from "../ApartmentsSection/apartmentReferences";
import {EntityModal} from "./EntityModal";
import {Board} from "./Board";
import {useCanEdit} from "../../../sectionAccess";

const flatsText = (n) => `${n} кв.`
const FROM_OLD_SITE = <Tag color='blue'>со старого сайта</Tag>

// A complex's queues → buildings → sections: add, rename, move, delete (only empty ones); a building's board opens
// with its flats. ?complex=<id> in the address picks the complex (the ЖК editor links here).
export const CheckerboardSection = () => {
  const canEdit = useCanEdit()
  const location = useLocation()
  const history = useHistory()
  const [complexes, setComplexes] = useState([])
  const [complexId, setComplexId] = useState(() => Number(new URLSearchParams(location.search).get('complex')) || undefined)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null)
  const [board, setBoard] = useState(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    loadComplexes().then(setComplexes).catch(() => setError('Не удалось загрузить список ЖК.'))
  }, [])

  useEffect(() => {
    if (!complexId) {
      setData(null)
      return
    }
    let alive = true
    setLoading(true)
    ApiCheckerboardService.fetchStructure(complexId)
      .then(({data: structure}) => {
        if (!alive) return
        setData(structure)
        setError(null)
      })
      .catch(() => alive && setError('Не удалось загрузить шахматку ЖК.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [complexId, tick])

  const reload = () => setTick((n) => n + 1)

  const pickComplex = (id) => {
    setComplexId(id)
    history.replace({pathname: location.pathname, search: id ? `?complex=${id}` : ''})
  }

  const remove = async (kind, id) => {
    try {
      await ApiCheckerboardService.remove(kind, id)
      message.success('Удалено')
      reload()
    } catch (e) {
      message.error(e?.response?.data?.errors?.message || 'Не удалось удалить')
    }
  }

  const queueModal = (queue) => setModal({
    title: queue ? `Очередь ${queue.name}` : 'Новая очередь',
    fields: [
      {key: 'name', label: 'Очередь', kind: 'text', required: true},
      {key: 'endingperiod', label: 'Срок сдачи', kind: 'text', extra: 'Например «IV кв. 2026»'},
    ],
    initial: queue ? {name: queue.name, endingperiod: queue.endingperiod} : {},
    save: (values) => ApiCheckerboardService.save('queue', queue && queue.id, queue ? values : {...values, complexId}),
  })

  const buildingModal = (queue, building) => setModal({
    title: building ? `Корпус ${building.number}` : `Новый корпус в очереди ${queue.name}`,
    fields: [
      {key: 'number', label: 'Корпус', kind: 'text', required: true},
      ...(building ? [{
        key: 'queueId',
        label: 'Очередь',
        kind: 'select',
        options: data.queues.map((q) => ({value: q.id, label: `Очередь ${q.name}${q.endingperiod ? ` · ${q.endingperiod}` : ''}`})),
      }] : []),
    ],
    initial: building ? {number: building.number, queueId: queue.id} : {},
    save: (values) => ApiCheckerboardService.save('building', building && building.id, building ? values : {...values, queueId: queue.id}),
  })

  const sectionModal = (building, section) => setModal({
    title: section ? `Секция ${section.name}` : `Новая секция в корпусе ${building.number}`,
    fields: [
      {
        key: 'name',
        label: 'Секция',
        kind: 'text',
        required: true,
        disabled: Boolean(section && section.eciOldFlats),
        extra: section && section.eciOldFlats ? 'В секции квартиры со старого сайта — её название берётся оттуда.' : null,
      },
      {key: 'totalFloors', label: 'Этажей', kind: 'count', max: 200, extra: 'Пусто — по квартирам секции'},
      {key: 'maximumInSection', label: 'Стояков (квартир на этаже)', kind: 'count', max: 60, extra: 'Пусто — по квартирам секции'},
    ],
    initial: section ? {name: section.name, totalFloors: section.totalFloors, maximumInSection: section.maximumInSection} : {},
    save: (values) => ApiCheckerboardService.save('section', section && section.id, section ? values : {...values, buildingId: building.id}),
  })

  return (
    <>
      <div className='section-toolbar cb-toolbar'>
        <Select
          showSearch
          allowClear
          optionFilterProp='label'
          style={{width: 320}}
          placeholder='Выберите ЖК'
          value={complexId}
          options={complexOptions(complexes)}
          onChange={pickComplex}
        />
        <div className='section-toolbar__spacer'/>
        {data && canEdit && <Button type='primary' icon={<PlusOutlined/>} onClick={() => queueModal(null)}>Добавить очередь</Button>}
      </div>
      {data && data.queues.some((q) => q.eciOldId) && (
        <Alert
          className='section-note'
          type='info'
          showIcon
          message='Очереди и корпуса со старого сайта привязаны по его id: названия, заданные здесь, остаются. Квартиры старого сайта сами раскладываются по секциям с названием как у них, поэтому такие секции не переименовываются. Удалить можно только пустое.'
        />
      )}
      {error && <Alert className='section-note' type='error' message={error}/>}
      {!complexId && <Empty description='Выберите ЖК'/>}
      {loading && !data && <div className='complex-editor__loading'><Spin/></div>}
      {data && !data.queues.length && <Empty description='У ЖК нет очередей'/>}
      {data && data.queues.map((queue) => (
        <section key={queue.id} className='cb-queue'>
          <header className='cb-queue__head'>
            <div>
              <b>Очередь {queue.name}</b>
              {queue.endingperiod && <span className='cell-hint'> · сдача {queue.endingperiod}</span>}
              <span className='cell-hint'> · {flatsText(queue.flats)}</span> {queue.eciOldId && FROM_OLD_SITE}
            </div>
            {canEdit && <Space wrap>
              <Button size='small' icon={<PlusOutlined/>} onClick={() => buildingModal(queue, null)}>Корпус</Button>
              <Button size='small' icon={<EditOutlined/>} onClick={() => queueModal(queue)}>Изменить</Button>
              <Popconfirm title='Удалить очередь?' okText='Удалить' cancelText='Нет' onConfirm={() => remove('queue', queue.id)} disabled={queue.buildings.length > 0}>
                <Button size='small' danger icon={<DeleteOutlined/>} disabled={queue.buildings.length > 0} title={queue.buildings.length ? 'Сначала удалите или перенесите корпуса' : ''}/>
              </Popconfirm>
            </Space>}
          </header>
          {!queue.buildings.length && <p className='cell-hint'>Корпусов нет.</p>}
          {queue.buildings.map((building) => (
            <div key={building.id} className='cb-building'>
              <div className='cb-building__head'>
                <div>
                  <b>Корпус {building.number}</b>
                  <span className='cell-hint'> · {flatsText(building.flats)}</span> {building.eciOldId && FROM_OLD_SITE}
                </div>
                <Space wrap>
                  <Button size='small' type='primary' ghost icon={<TableOutlined/>} onClick={() => setBoard(building)} disabled={!building.flats}>Шахматка</Button>
                  {canEdit && (
                    <>
                      <Button size='small' icon={<PlusOutlined/>} onClick={() => sectionModal(building, null)}>Секция</Button>
                      <Button size='small' icon={<EditOutlined/>} onClick={() => buildingModal(queue, building)}>Изменить</Button>
                      <Popconfirm title='Удалить корпус?' okText='Удалить' cancelText='Нет' onConfirm={() => remove('building', building.id)} disabled={building.flats > 0}>
                        <Button size='small' danger icon={<DeleteOutlined/>} disabled={building.flats > 0} title={building.flats ? 'В корпусе есть квартиры' : ''}/>
                      </Popconfirm>
                    </>
                  )}
                </Space>
              </div>
              <div className='cb-sections'>
                {building.sections.map((section) => (
                  <span key={section.id} className='cb-section-tag'>
                    <button type='button' onClick={() => canEdit && sectionModal(building, section)} title={canEdit ? 'Изменить секцию' : ''} disabled={!canEdit}>
                      Секция {section.name}: {section.totalFloors || '—'} эт., {section.maximumInSection || '—'} ст., {flatsText(section.flats)}
                    </button>
                    {!section.flats && canEdit && (
                      <Popconfirm title='Удалить пустую секцию?' okText='Удалить' cancelText='Нет' onConfirm={() => remove('section', section.id)}>
                        <button type='button' className='cb-section-tag__remove' aria-label='Удалить секцию'>×</button>
                      </Popconfirm>
                    )}
                  </span>
                ))}
                {!building.sections.length && <span className='cell-hint'>Секций нет</span>}
              </div>
            </div>
          ))}
        </section>
      ))}
      {modal && (
        <EntityModal
          {...modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            reload()
          }}
        />
      )}
      {board && <Board building={board} complexes={complexes} onClose={() => { setBoard(null); reload() }}/>}
    </>
  )
}
